import { DispatcherEvent } from '@carvana/merch-native';
import { useContext, useMemo } from 'react';
import { setCorrelationContext } from '~/clients';
import UserContext from '~/context/common/UserContext';
import type { StepConfiguration } from '~/contracts/common';
import {
  useCloseAccountModal,
  useOpenAccountModal
} from '~/hooks/common/useAccountModal';
import { fetchUserProfile } from '~/services/common';
import { logError } from '~/utilities/loggers';
import type { AddFavoritesFlow, FavoritesArguments } from './types';

const defaultModalSettings: StepConfiguration = {
  displayHeading: 'SAVE TO FAVORITES',
  displayTitle: 'SAVE TO FAVORITES'
};

async function checkHasUserProfile(
  controller: AbortController = new AbortController()
) {
  try {
    const { customerId: userId } = await fetchUserProfile(controller);

    if (userId) {
      setCorrelationContext(userId);
    }

    return Boolean(userId);
  } catch (err) {
    logError(err, { location: 'Check Has User Profile' });
  }
}

export default function useLogInAndFavorite(): AddFavoritesFlow {
  const {
    userState: {
      data: { isLoggedIn }
    }
  } = useContext(UserContext);
  const openAccountModal = useOpenAccountModal();
  const closeAccountModal = useCloseAccountModal();

  return useMemo(
    () =>
      (onContinue: (args: FavoritesArguments) => void) =>
      async (args: FavoritesArguments) => {
        if (isLoggedIn) {
          onContinue(args);

          return;
        }

        const { modalSettings = defaultModalSettings } = args;

        openAccountModal({
          ...modalSettings,
          callerIdentifier: 'Favorites',
          nativeOptions: args.nativeEventSource
            ? {
                eventPayload: {
                  stockNumber: args.stockNumber,
                  subscribeToNotifications: args.subscribeToNotifications,
                  vehicleId: args.vehicleId
                },
                eventDispatch: DispatcherEvent.AddFavorite,
                eventSource: args.nativeEventSource
              }
            : undefined,
          onSuccess: async () => {
            const hasUserProfile = await checkHasUserProfile();

            closeAccountModal();

            if (hasUserProfile) {
              onContinue(args);
            }
          }
        });
      },
    [closeAccountModal, isLoggedIn, openAccountModal]
  );
}
